import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { httpsCallable } from "firebase/functions";
import {
  CheckCircle,
  Gear,
  PlugsConnected,
  Receipt,
  UsersThree,
  WarningCircle,
} from "@phosphor-icons/react";
import { functions } from "../../firebase";
import {
  selectClinicId,
  selectClinicProfile,
  setClinicProfile,
} from "../../store/slices/authSlice";
import { useToast } from "../../contexts/ToastContext";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import {
  MORNING_BUSINESS_TYPE_LABELS,
  MORNING_DOCUMENT_HINTS,
  MORNING_PAYMENT_TYPES,
  getAllowedDocumentTypeOptions,
  getPaymentTypeOptions,
  isMorningExempt,
  resolveClinicDefaultDocumentType,
} from "../../services/morningService";
import "./MorningSettings.css";

/**
 * MorningSettingsModal — edit Morning issuing preferences after connect,
 * WITHOUT re-entering the API key. Saves via `updateMorningSettings`;
 * "בדיקת חיבור" calls `testMorningConnection` (refreshes business facts).
 *
 * Error codes: CK-8814 (save), CK-8815 (test connection)
 */
export default function MorningSettingsModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const { showSuccess } = useToast();

  const mi = clinicProfile?.morningIntegration || null;
  const exempt = isMorningExempt(mi);

  const buildForm = (settings) => ({
    defaultDocumentType: resolveClinicDefaultDocumentType(settings),
    defaultPaymentType:
      Number(settings?.defaultPaymentType) || MORNING_PAYMENT_TYPES.CREDIT_CARD,
    vatIncluded: settings?.vatIncluded !== false,
    allowTherapistsToCreateInvoices:
      settings?.allowTherapistsToCreateInvoices === true,
  });

  const [form, setForm] = useState(() => buildForm(mi));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setForm(buildForm(mi));
      setError("");
      setTestResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mi]);

  const docOptions = useMemo(
    () => getAllowedDocumentTypeOptions(mi?.allowedDocumentTypes, exempt),
    [mi, exempt]
  );

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const applyIntegration = (morningIntegration) => {
    dispatch(
      setClinicProfile({
        ...clinicProfile,
        morningIntegration: morningIntegration || clinicProfile.morningIntegration,
      })
    );
  };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const updateMorningSettings = httpsCallable(functions, "updateMorningSettings");
      const res = await updateMorningSettings({ clinicId, settings: form });
      if (!res.data?.success) throw new Error(res.data?.error || "שמירה נכשלה");
      applyIntegration(res.data.morningIntegration);
      showSuccess("הגדרות מורנינג נשמרו");
      onClose();
    } catch (err) {
      console.error("[CK-8814] Morning settings save failed", {
        errorCode: "CK-8814",
        errorMessage: err.message,
        timestamp: new Date().toISOString(),
      });
      setError(err.message || "שגיאה בשמירת ההגדרות");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setError("");
    setTestResult(null);
    setTesting(true);
    try {
      const testMorningConnection = httpsCallable(functions, "testMorningConnection");
      const res = await testMorningConnection({ clinicId });
      if (!res.data?.success) {
        setTestResult({ ok: false, message: res.data?.error || "החיבור נכשל" });
        return;
      }
      applyIntegration(res.data.morningIntegration);
      setTestResult({
        ok: true,
        message: `החיבור תקין${res.data.businessName ? ` · ${res.data.businessName}` : ""}${
          res.data.isExempt ? " · עוסק פטור" : ""
        }`,
      });
    } catch (err) {
      console.error("[CK-8815] Morning connection test failed", {
        errorCode: "CK-8815",
        errorMessage: err.message,
        timestamp: new Date().toISOString(),
      });
      setTestResult({ ok: false, message: err.message || "שגיאה בבדיקת החיבור" });
    } finally {
      setTesting(false);
    }
  };

  if (!isOpen) return null;
  const busy = saving || testing;
  const lastValidated = mi?.lastValidatedAt
    ? new Date(mi.lastValidatedAt).toLocaleDateString("he-IL")
    : null;

  return (
    <div className="mset-overlay" onClick={onClose} role="presentation">
      <div
        className="mset-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mset-title"
      >
        <div className="mset-header">
          <div className="mset-title">
            <span className="mset-icon">
              <Gear size={20} weight="fill" />
            </span>
            <div>
              <span className="mset-eyebrow">מורנינג</span>
              <h3 id="mset-title">הגדרות הפקת מסמכים</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="mset-body">
          {/* Business facts strip */}
          <div className="mset-facts">
            <div className="mset-fact">
              <span className="mset-fact-label">עסק</span>
              <span className="mset-fact-value">{mi?.businessName || clinicProfile?.name || "-"}</span>
            </div>
            <div className="mset-fact">
              <span className="mset-fact-label">סוג עוסק</span>
              <span className="mset-fact-value">
                {MORNING_BUSINESS_TYPE_LABELS[mi?.businessType] || mi?.businessType || "-"}
                {exempt && <span className="mset-badge">פטור ממע״מ</span>}
              </span>
            </div>
            <div className="mset-fact">
              <span className="mset-fact-label">בדיקה אחרונה</span>
              <span className="mset-fact-value">{lastValidated || "לא ידוע"}</span>
            </div>
          </div>

          <div className="mset-field">
            <label htmlFor="mset-doc-type">
              <Receipt size={16} weight="fill" />
              סוג מסמך ברירת מחדל
            </label>
            <select
              id="mset-doc-type"
              value={form.defaultDocumentType}
              onChange={(e) => set({ defaultDocumentType: Number(e.target.value) })}
              disabled={busy}
            >
              {docOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="mset-hint">
              {MORNING_DOCUMENT_HINTS[form.defaultDocumentType] || ""}
              {Array.isArray(mi?.allowedDocumentTypes) && mi.allowedDocumentTypes.length > 0
                ? " הרשימה מסוננת לפי מה שהעסק שלך יכול להפיק במורנינג."
                : ""}
            </span>
          </div>

          <div className="mset-field">
            <label htmlFor="mset-pay-type">אמצעי תשלום ברירת מחדל</label>
            <select
              id="mset-pay-type"
              value={form.defaultPaymentType}
              onChange={(e) => set({ defaultPaymentType: Number(e.target.value) })}
              disabled={busy}
            >
              {getPaymentTypeOptions().map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="mset-hint">
              בהפקה מתוך תשלום שנרשם, אמצעי התשלום נלקח מהתשלום עצמו.
            </span>
          </div>

          {!exempt && (
            <label className={`mset-toggle ${form.vatIncluded ? "on" : ""}`}>
              <input
                type="checkbox"
                checked={form.vatIncluded}
                onChange={(e) => set({ vatIncluded: e.target.checked })}
                disabled={busy}
              />
              <span className="mset-toggle-text">
                <strong>המחירים ב-CliniKit כוללים מע״מ</strong>
                <span>
                  {form.vatIncluded
                    ? "המסמך יציג את הסכום כולל מע״מ (מומלץ)"
                    : "מורנינג יוסיף מע״מ מעל הסכום שהוזן"}
                </span>
              </span>
              <span className="mset-switch" aria-hidden="true" />
            </label>
          )}

          <label className={`mset-toggle ${form.allowTherapistsToCreateInvoices ? "on" : ""}`}>
            <input
              type="checkbox"
              checked={form.allowTherapistsToCreateInvoices}
              onChange={(e) => set({ allowTherapistsToCreateInvoices: e.target.checked })}
              disabled={busy}
            />
            <span className="mset-toggle-text">
              <strong>
                <UsersThree size={16} weight="fill" /> מטפלים יכולים להפיק מסמכים
              </strong>
              <span>כבוי: רק מנהלי הקליניקה מפיקים חשבוניות וקבלות</span>
            </span>
            <span className="mset-switch" aria-hidden="true" />
          </label>

          {testResult && (
            <div className={`mset-callout ${testResult.ok ? "ok" : "warn"}`}>
              {testResult.ok ? (
                <CheckCircle size={18} weight="fill" />
              ) : (
                <WarningCircle size={18} weight="fill" />
              )}
              <span>{testResult.message}</span>
            </div>
          )}
          {error && (
            <div className="mset-callout warn">
              <WarningCircle size={18} weight="fill" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="mset-footer">
          <button
            type="button"
            className="mset-btn secondary"
            onClick={testConnection}
            disabled={busy}
            title="מאמת את המפתח ומרענן את פרטי העסק ממורנינג"
          >
            {testing ? <LoaderLoop size="xs" /> : <PlugsConnected size={16} />}
            בדיקת חיבור
          </button>
          <button type="button" className="mset-btn primary" onClick={save} disabled={busy}>
            {saving ? <LoaderLoop size="xs" /> : <CheckCircle size={16} weight="bold" />}
            שמירה
          </button>
        </div>
      </div>
    </div>
  );
}
